// 是否为生产环境
const isProduction = import.meta.env.PROD;

/**
 * 上报错误到监控系统
 * @param {Object} errorInfo 错误信息
 */
function reportError(errorInfo) {
  // TODO: 接入错误监控系统（如 Sentry、Bugsnag 等）
  // 这里可以发送到你的后端 API
  fetch('/api/errors', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(errorInfo),
  }).catch((err) => {
    console.error('错误上报失败:', err);
  });

  // 也可以在控制台显示友好的错误提示

  console.group('🐛 错误详情');
  console.error('类型:', errorInfo.type);
  console.error('信息:', errorInfo);
  console.error('URL:', errorInfo.url);
  console.groupEnd();
}

export default (app) => {
  /**
   * 全局错误捕获
   */
  function setupGlobalErrorHandler() {
    // 1. Vue 应用级错误处理器
    app.config.errorHandler = (err, instance, info) => {
      console.error('Vue Error:', err);
      console.error('Component:', info);
      console.error('Instance:', instance);

      // 生产环境可以上报错误到监控系统
      if (isProduction) {
        reportError({
          type: 'vue-error',
          error: err,
          info,
          url: window.location.href,
          userAgent: navigator.userAgent,
        });
      }
    };

    // 2. Vue 警告处理器
    app.config.warnHandler = (msg, instance, trace) => {
      console.warn('Vue Warning:', msg);
      console.warn('Trace:', trace);

      if (isProduction) {
        reportError({
          type: 'vue-warning',
          message: msg,
          trace,
          url: window.location.href,
          userAgent: navigator.userAgent,
        });
      }
    };

    // 3. 未捕获的 Promise 错误
    window.addEventListener('unhandledrejection', (event) => {
      console.error('Unhandled Promise Rejection:', event.reason);

      if (isProduction) {
        event.preventDefault(); // 阻止默认的控制台错误
        reportError({
          type: 'unhandled-rejection',
          reason: event.reason,
          promise: event.promise,
          url: window.location.href,
          userAgent: navigator.userAgent,
        });
      }
    });

    // 4. 全局 JavaScript 错误
    window.addEventListener('error', (event) => {
      console.error('Global Error:', event.error);
      console.error('Message:', event.message);
      console.error('Filename:', event.filename);
      console.error('Line:', event.lineno);
      console.error('Column:', event.colno);

      if (isProduction) {
        reportError({
          type: 'global-error',
          error: event.error,
          message: event.message,
          filename: event.filename,
          line: event.lineno,
          column: event.colno,
          url: window.location.href,
          userAgent: navigator.userAgent,
        });
      }
    });

    // 5. 资源加载错误
    window.addEventListener(
      'error',
      (event) => {
        if (event.target !== window) {
          // 资源加载错误（图片、脚本等）
          const target = event.target;
          console.error('Resource Load Error:', {
            tagName: target.tagName,
            src: target.src,
            href: target.href,
          });

          if (isProduction) {
            reportError({
              type: 'resource-error',
              tagName: target.tagName,
              src: target.src || target.href,
              url: window.location.href,
              userAgent: navigator.userAgent,
            });
          }
        }
      },
      true
    ); // 使用捕获阶段

    console.log(
      isProduction ? '生产环境错误监控已启用' : '开发环境错误监控已启用'
    );
  }

  /**
   * 开发环境显示错误提示
   */
  function showErrorAlert(message) {
    if (!isProduction) {
      // 开发环境在页面显示错误提示
      const alertDiv = document.createElement('div');
      alertDiv.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      max-width: 400px;
      padding: 15px;
      background: #ff4d4f;
      color: white;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.15);
      font-size: 14px;
      z-index: 9999;
      animation: slideIn 0.3s ease;
    `;
      alertDiv.innerHTML = `
      <strong style="display: block; margin-bottom: 8px;">🐛 错误发生</strong>
      <span style="opacity: 0.9;">${message}</span>
    `;
      document.body.appendChild(alertDiv);

      // 3秒后自动消失
      setTimeout(() => {
        alertDiv.style.opacity = '0';
        setTimeout(() => {
          document.body.removeChild(alertDiv);
        }, 300);
      }, 3000);
    }
  }

  // 全局属性方法，供组件使用
  app.config.globalProperties.$showError = showErrorAlert;
  app.config.globalProperties.$reportError = reportError;

  // 设置全局错误处理
  setupGlobalErrorHandler();

  // 应用挂载后的全局错误处理
  if (isProduction) {
    // 监控应用性能
    if ('PerformanceObserver' in window) {
      const observer = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          if (entry.entryType === 'navigation') {
            console.log('页面加载性能:', {
              加载时间: Math.round(entry.duration),
              'DOM 解析': Math.round(
                entry.domContentLoadedEventEnd - entry.fetchStart
              ),
              首次绘制: Math.round(entry.loadEventEnd - entry.fetchStart),
            });
          }
        }
      });
      observer.observe({ entryTypes: ['navigation'] });
    }

    // 监控内存使用
    setInterval(() => {
      if (performance.memory) {
        const memory = performance.memory;
        const usedMB = Math.round(memory.usedJSHeapSize / 1048576);
        const totalMB = Math.round(memory.jsHeapSizeLimit / 1048576);
        const percent = Math.round((usedMB / totalMB) * 100);

        if (percent > 80) {
          console.warn(
            `⚠️ 内存使用率: ${percent}% (${usedMB}MB / ${totalMB}MB)`
          );
        }
      }
    }, 60000); // 每分钟检查一次
  }
};
